import { cn } from '@shared/lib/cn';
import { haptics } from '@shared/lib/haptics';

/**
 * iOS-style pill segmented control. The active indicator is local to each
 * control, so multiple segmented controls can sit together without their
 * animations affecting each other.
 */
interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  fullWidth?: boolean;
  tone?: 'default' | 'inverse';
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  fullWidth = false,
  tone = 'default',
}: SegmentedControlProps<T>): JSX.Element {
  const activeIndex = Math.max(0, options.findIndex((opt) => opt.value === value));
  const inverse = tone === 'inverse';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-grid rounded-pill p-1',
        inverse ? 'bg-white/10' : 'bg-sunken',
        fullWidth && 'w-full',
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden
        className={cn(
          'absolute bottom-1 top-1 rounded-pill transition-transform duration-fast ease-smooth',
          inverse ? 'bg-white shadow-sm' : 'bg-card hairline',
        )}
        style={{
          left: '0.25rem',
          width: `calc((100% - 0.5rem) / ${options.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              // Tick only when the choice changes, not on re-tapping the active one.
              if (!active) haptics.tap();
              onChange(opt.value);
            }}
            className={cn(
              'relative z-10 rounded-pill px-4 py-1.5 text-label transition-colors duration-fast',
              fullWidth && 'py-2 text-body-sm',
              inverse
                ? active
                  ? 'text-[color:var(--primary-900)]' // Sits on the white indicator, not the header's surface.
                  : 'text-white/70 hover:text-white'
                : active
                  ? 'text-ink'
                  : 'text-ink-muted hover:text-ink-secondary',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
